import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

/** Compact design tokens shared by the WhatsApp settings screens. */
export const UI = {
  gold: '#9d6f32',
  goldHover: '#86602b',
  goldTint: '#f6efe5',
  ink: '#1c1b19',
  muted: '#6f6b65',
  faint: '#9b968f',
  line: '#ebe8e3',
  surface: '#fdfcfa',
  canvas: '#f7f6f3',
  body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  heading: "'Georgia', 'Times New Roman', serif",
  mono: "'SFMono-Regular', Menlo, Consolas, monospace",
};

/** Small, dense MUI overrides: 32px inputs, 13px text. */
export const denseInputSx = {
  '& .MuiInputBase-root': { fontSize: 13, height: 32 },
  '& .MuiInputBase-input': { py: 0.5 },
  '& .MuiInputLabel-root': { fontSize: 13 },
};

export const textButtonSx = {
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 500,
  color: UI.ink,
  borderColor: UI.line,
  minHeight: 30,
  px: 1.5,
  py: 0.25,
  '&:hover': { borderColor: UI.faint, bgcolor: UI.canvas },
} as const;

export const primaryButtonSx = {
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 600,
  bgcolor: UI.gold,
  minHeight: 30,
  px: 2,
  py: 0.25,
  boxShadow: 'none',
  '&:hover': { bgcolor: UI.goldHover, boxShadow: 'none' },
} as const;

export type PillTone = 'green' | 'amber' | 'slate';

const PILL_TONES: Record<PillTone, { bg: string; fg: string; dot: string }> = {
  green: { bg: '#edf6ee', fg: '#2f6b35', dot: '#3f9a48' },
  amber: { bg: '#fbf3e6', fg: '#8a5a12', dot: '#d08a1e' },
  slate: { bg: '#f0f0ee', fg: '#4f4c47', dot: '#8c8780' },
};

export const StatusPill = ({ tone, children }: { tone: PillTone; children: React.ReactNode }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={0.75}
    sx={{
      px: 1,
      py: 0.25,
      borderRadius: 999,
      bgcolor: PILL_TONES[tone].bg,
      color: PILL_TONES[tone].fg,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      minWidth: 0,
    }}
  >
    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: PILL_TONES[tone].dot, flexShrink: 0 }} />
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {children}
    </Box>
  </Stack>
);

/** Label on the left, control on the right; stacks on small screens. */
export const SettingRow = ({
  label,
  hint,
  children,
  last,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
  last?: boolean;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '180px minmax(0, 1fr)' },
      columnGap: 3,
      rowGap: 1,
      py: 1.75,
      borderBottom: last ? 'none' : `1px solid ${UI.line}`,
    }}
  >
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: UI.ink, lineHeight: 1.4 }}>{label}</Typography>
      {hint && <Typography sx={{ fontSize: 12, color: UI.muted, mt: 0.25, lineHeight: 1.45 }}>{hint}</Typography>}
    </Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

/** Pill-style day toggles (Mon..Sun). */
export const DayToggles = <K extends string>({
  days,
  labels,
  isOn,
  isTaken,
  onToggle,
}: {
  days: K[];
  labels: Record<K, string>;
  isOn: (day: K) => boolean;
  /** Day is used by another mode: still clickable (moves it), shown dimmed. */
  isTaken?: (day: K) => boolean;
  onToggle: (day: K) => void;
}) => (
  <Stack direction="row" gap={0.5} flexWrap="wrap">
    {days.map((day) => {
      const on = isOn(day);
      const taken = !on && isTaken?.(day);
      return (
        <Box
          key={day}
          component="button"
          type="button"
          aria-pressed={on}
          onClick={() => onToggle(day)}
          sx={{
            fontFamily: UI.body,
            fontSize: 12.5,
            fontWeight: on ? 600 : 500,
            minWidth: 42,
            height: 28,
            px: 1,
            borderRadius: 1.5,
            cursor: 'pointer',
            border: `1px solid ${on ? UI.gold : UI.line}`,
            bgcolor: on ? UI.goldTint : 'transparent',
            color: on ? UI.gold : taken ? UI.faint : UI.ink,
            transition: 'background-color 120ms cubic-bezier(0.25, 1, 0.5, 1), border-color 120ms cubic-bezier(0.25, 1, 0.5, 1)',
            '&:hover': { borderColor: on ? UI.gold : UI.faint },
            '&:focus-visible': { outline: `2px solid ${UI.gold}`, outlineOffset: 1 },
          }}
        >
          {labels[day]}
        </Box>
      );
    })}
  </Stack>
);
